import { NetworkStatus, useQuery } from '@apollo/client';
import { useFocusEffect, useNavigation, useRoute } from '@react-navigation/native';
import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ScrollView } from '@oui/app-core/src/components/ScrollView';
import { View } from '@oui/app-core/src/components/View';
import { useArtifactRequest } from '@oui/app-core/src/hooks/useArtifactResult';
import { graphql } from '@oui/lib/src/graphql/tada';

import { StressBreathingExplanation } from '../../components/StressBreathingExplanation/StressBreathingExplanation';
import {
  StressBreathingOverview,
  StressBreathingOverviewFragment,
} from '../../components/StressBreathingOverview/StressBreathingOverview';
import { StackScreenProps } from '../../types/navigation';

export const StressBreathingIndexQuery = graphql(
  `
    query StressBreathingIndex($hyperventilation: Boolean!, $breathHolding: Boolean!) {
      user {
        ID
        role {
          ID
          ...StressBreathingOverview
          breathHoldingWidget @include(if: $breathHolding) {
            ID
            stressBreathingExercises(input: {}) {
              practiceID
              stressBreathingExercise {
                isComplete
              }
            }
          }
          hyperventilationWidget @include(if: $breathHolding) {
            ID
            stressBreathingExercises(input: {}) {
              practiceID
              stressBreathingExercise {
                isComplete
              }
            }
          }
        }
      }
    }
  `,
  [StressBreathingOverviewFragment],
);

/**
 * Has an initial state the first time the user does the exercise and a subsequent state every time after.
 */
export const StressBreathingIndex = ({
  _mockInitialNumCompletedRef,
}: {
  _mockInitialNumCompletedRef?: number;
}) => {
  const { setOptions } = useNavigation<StackScreenProps<'StressBreathingIndex'>['navigation']>();
  const route = useRoute<StackScreenProps<'StressBreathingIndex'>['route']>();
  const insets = useSafeAreaInsets();
  const type = route.params?.type ?? 'holding';
  const { data, refetch, networkStatus } = useQuery(StressBreathingIndexQuery, {
    variables: { breathHolding: type === 'holding', hyperventilation: type === 'hyperventilation' },
  });
  const initialNumCompletedRef = useRef<number | undefined>(_mockInitialNumCompletedRef);

  const exercises =
    data?.user?.role?.breathHoldingWidget?.stressBreathingExercises ||
    data?.user?.role?.hyperventilationWidget?.stressBreathingExercises;
  const hasExercises = !!exercises?.length;
  const numCompleted = exercises?.filter((e) => e.stressBreathingExercise.isComplete).length;

  if (
    typeof numCompleted === 'number' &&
    typeof initialNumCompletedRef.current !== 'number' &&
    networkStatus === NetworkStatus.ready
  ) {
    initialNumCompletedRef.current = numCompleted;
  }

  // mark widget as completed if an exercise has been completed since we opened the widget
  const isComplete =
    typeof numCompleted === 'number' &&
    typeof initialNumCompletedRef.current === 'number' &&
    numCompleted > initialNumCompletedRef.current;
  const { hasRequest: hasArtifactRequest, navigateToConversation } = useArtifactRequest(
    type === 'holding' ? 'BreathHolding' : 'Hyperventilation',
    isComplete,
  );

  const shouldReturnToConversation = isComplete && hasArtifactRequest;

  useEffect(() => {
    if (shouldReturnToConversation) {
      navigateToConversation();
    }
  }, [shouldReturnToConversation, navigateToConversation]);

  useLayoutEffect(() => {
    setOptions({ headerTitle: '', headerShadowVisible: false });
  }, [setOptions]);

  useFocusEffect(
    useCallback(() => {
      void refetch();
    }, [refetch]),
  );

  return (
    <ScrollView
      testID="StressBreathingIndex"
      contentContainerStyle={{ paddingBottom: insets.bottom }}
    >
      <View style={{ paddingHorizontal: 16, paddingBottom: 45 }}>
        <StressBreathingExplanation type={type} detailedExplanation={!hasExercises} />
      </View>
      {data?.user?.role && hasExercises ? (
        <StressBreathingOverview type={type} data={data.user.role} />
      ) : null}
    </ScrollView>
  );
};
